import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mic, Video, Check, Loader, ShieldCheck, Bell, Share } from 'lucide-react';
import { toast } from 'sonner';
import { ensurePushSubscribed } from '../hooks/usePushRegistration';
import { pushSupportState } from '../lib/platform';

/**
 * One-time post-signup interstitial that asks the new user to grant
 * microphone + camera access so future voice/video consultations work without
 * the user having to deal with a confusing in-call permission prompt.
 *
 * The page calls navigator.mediaDevices.getUserMedia() to trigger the native
 * browser permission dialog, then immediately stops the tracks (we don't need
 * them at sign-up time, we only need the permission grant).
 *
 * Either choice (Allow or Skip) sets the localStorage flag tababa_perm_asked=1
 * so this screen never shows again for that browser / account.
 */
export const PermissionsPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const isRTL = language === 'ar';
  const [status, setStatus] = useState('idle'); // idle | requesting | granted | denied
  // 'ready' | 'ios-install' | 'unsupported'
  const [pushState] = useState(() => pushSupportState());


  const markAsked = () => {
    try { localStorage.setItem('tababa_perm_asked', '1'); } catch (e) { /* ignore */ }
  };

  // Ask for browser notification permission and register the push
  // subscription so appointment / call alerts reach the user even when the
  // app is closed. Best-effort — never blocks the media permission flow.
  const requestNotifications = async () => {
    try {
      if (pushState !== 'ready') return; // iOS in Safari can't be asked yet
      if (!('Notification' in window)) return;
      if (Notification.permission === 'default') {
        await Notification.requestPermission();
      }
      if (Notification.permission === 'granted') {
        await ensurePushSubscribed();
      }
    } catch (e) { /* ignore */ }
  };

  const handleAllow = async () => {
    setStatus('requesting');
    await requestNotifications();
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('media-not-supported');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      // Immediately release the hardware — we only needed the permission grant
      stream.getTracks().forEach((track) => track.stop());
      setStatus('granted');
      try { localStorage.setItem('tababa_perm_status', 'granted'); } catch (e) { /* ignore */ }
      markAsked();
      toast.success(t('تم منح الإذن بنجاح!', 'Permissions granted!', 'Autorisations accordées !'));
      setTimeout(() => navigate('/', { replace: true }), 900);
    } catch (e) {
      setStatus('denied');
      // We still mark the question as asked so we don't keep nagging the user
      markAsked();
      toast.error(t('لم يتم منح الإذن. يمكنك تفعيله لاحقاً من إعدادات المتصفح.',
        'Permission not granted. You can enable it later from your browser settings.',
        "Autorisation refusée. Activez-la plus tard dans les paramètres."));
    }
  };

  const handleSkip = () => {
    markAsked();
    try { localStorage.setItem('tababa_perm_status', 'skipped'); } catch (e) { /* ignore */ }
    navigate('/', { replace: true });
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b from-[#e3f2fd] via-white to-[#bbdefb] flex items-center justify-center px-4 py-10 ${isRTL ? 'rtl' : 'ltr'}`}
         dir={isRTL ? 'rtl' : 'ltr'}
         data-testid="permissions-page">
      <Card className="w-full max-w-md p-7 md:p-8 rounded-3xl shadow-xl border-0">
        {/* Hero icon */}
        <div className="flex justify-center mb-5">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#1976d2] to-[#0d47a1] flex items-center justify-center shadow-lg">
              <ShieldCheck size={36} className="text-white" />
            </div>
            <span className="absolute -bottom-1 -right-1 w-9 h-9 bg-white rounded-full shadow flex items-center justify-center">
              <Mic size={14} className="text-[#1565c0]" />
            </span>
            <span className="absolute -bottom-1 -left-1 w-9 h-9 bg-white rounded-full shadow flex items-center justify-center">
              <Video size={14} className="text-[#1565c0]" />
            </span>
          </div>
        </div>

        <h1 className="text-xl md:text-2xl font-extrabold text-[#0d47a1] text-center mb-2" data-testid="permissions-title">
          {pushState === 'ready'
            ? t('السماح بالميكروفون والكاميرا والإشعارات', 'Allow Microphone, Camera & Notifications', 'Autoriser micro, caméra & notifications')
            : t('السماح بالميكروفون والكاميرا', 'Allow Microphone & Camera', 'Autoriser le micro & la caméra')}
        </h1>

        <p className="text-sm md:text-base text-gray-600 text-center leading-relaxed mb-6">
          {t(
            'لاستخدام المكالمات الصوتية والمرئية مع الأطباء، يحتاج التطبيق إلى إذنك للوصول إلى الميكروفون والكاميرا. لن نسجل أو نشارك أي شيء بدون موافقتك.',
            'To use voice and video consultations with doctors, the app needs your permission to access the microphone and camera. We never record or share anything without your consent.',
            "Pour les consultations vocales et vidéo, l'application a besoin d'accéder à votre micro et caméra. Rien n'est enregistré ni partagé sans votre accord."
          )}
        </p>

        {/* Two feature rows */}
        <div className="space-y-3 mb-6">
          <div className={`flex items-center gap-3 p-3 rounded-xl bg-blue-50 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
            <Mic size={20} className="text-[#1565c0] flex-shrink-0" />
            <div className="text-sm text-gray-700">
              {t('للاستشارات الصوتية مع الأطباء', 'For voice consultations with doctors', 'Pour les consultations vocales')}
            </div>
          </div>
          <div className={`flex items-center gap-3 p-3 rounded-xl bg-blue-50 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
            <Video size={20} className="text-[#1565c0] flex-shrink-0" />
            <div className="text-sm text-gray-700">
              {t('للاستشارات المرئية مع الأطباء', 'For video consultations with doctors', 'Pour les consultations vidéo')}
            </div>
          </div>
          {pushState === 'ready' && (
            <div className={`flex items-center gap-3 p-3 rounded-xl bg-emerald-50 ${isRTL ? 'flex-row-reverse text-right' : ''}`}
                 data-testid="permissions-notifications-row">
              <Bell size={20} className="text-emerald-600 flex-shrink-0" />
              <div className="text-sm text-gray-700">
                {t('لتلقّي إشعارات المواعيد والمكالمات حتى لو كان التطبيق مغلقاً',
                   'To receive appointment & call alerts even when the app is closed',
                   'Pour recevoir les alertes même app fermée')}
              </div>
            </div>
          )}

          {pushState === 'ios-install' && (
            <div className={`p-3 rounded-xl bg-amber-50 border border-amber-200 ${isRTL ? 'text-right' : ''}`}
                 data-testid="permissions-ios-install-note">
              <div className={`flex items-center gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Share size={17} className="text-amber-600 flex-shrink-0" />
                <p className="text-sm font-bold text-amber-900">
                  {t('لتلقّي الإشعارات على iPhone', 'To get notifications on iPhone', 'Notifications sur iPhone')}
                </p>
              </div>
              <p className="text-[13px] text-amber-800 leading-snug">
                {t(
                  'اضغط زر المشاركة في Safari ثم «إضافة إلى الشاشة الرئيسية»، وافتح Tababa.co من الشاشة الرئيسية — هذه هي الطريقة الوحيدة التي تسمح بها Apple بوصول الإشعارات والتطبيق مغلق.',
                  'Tap the Share button in Safari, choose "Add to Home Screen", then open Tababa.co from your home screen — Apple only allows notifications from the installed app.',
                  'Appuyez sur Partager puis « Sur l’écran d’accueil », et ouvrez Tababa.co depuis l’écran d’accueil.',
                )}
              </p>
              <a href="/install" className="text-[13px] font-bold text-[#0d47a1] underline mt-1 inline-block"
                 data-testid="permissions-install-guide-link">
                {t('شاهد خطوات التثبيت بالصور', 'See the illustrated install steps')}
              </a>
            </div>
          )}
        </div>

        {/* Buttons */}
        <Button
          onClick={handleAllow}
          disabled={status === 'requesting'}
          className="w-full h-12 bg-[#1565c0] hover:bg-[#0d47a1] rounded-xl font-bold text-base mb-3"
          data-testid="permissions-allow-button"
        >
          {status === 'requesting' && <Loader className="animate-spin me-2" size={18} />}
          {status === 'granted' && <Check className="me-2" size={18} />}
          {status === 'requesting'
            ? t('جاري الطلب...', 'Requesting...', 'Demande...')
            : status === 'granted'
              ? t('تم!', 'Done!', 'OK !')
              : t('السماح الآن', 'Allow Now', 'Autoriser')}
        </Button>

        <Button
          onClick={handleSkip}
          variant="outline"
          disabled={status === 'requesting'}
          className="w-full rounded-xl"
          data-testid="permissions-skip-button"
        >
          {t('لاحقاً', 'Maybe Later', 'Plus tard')}
        </Button>

        <p className="text-xs text-gray-400 text-center mt-5 leading-relaxed">
          {t(
            'يمكنك دائماً تغيير هذه الأذونات لاحقاً من إعدادات متصفحك.',
            'You can always change these permissions later from your browser settings.',
            "Vous pouvez modifier ces autorisations plus tard."
          )}
        </p>
      </Card>
    </div>
  );
};

export default PermissionsPage;
